import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import VideoPlayer from "@/components/video-player";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTitle } from "@/hooks/use-title";

interface Video {
  id: number;
  videoId: string;
  title: string;
  thumbnail: string;
  categoryId: number;
  sortOrder: number;
}

export default function Watch() {
  const [, setLocation] = useLocation();
  const { videoId } = useParams();

  const { data: video, isLoading, error } = useQuery<Video>({
    queryKey: [`/api/videos/${videoId}`],
    enabled: !!videoId,
  });

  useTitle(video?.title || "Watch");

  if (!videoId) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black">
      {/* Navigation bar with back button */}
      <div className="fixed top-0 left-0 right-0 bg-black/50 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/10 transition-colors"
            onClick={() => setLocation("/")}
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center h-[60vh]">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center p-8 bg-red-500/10 rounded-lg">
              <p className="text-lg font-semibold">Error loading video</p>
              <p className="text-sm opacity-80">Please try again later</p>
            </div>
          ) : video ? (
            <div className="space-y-6">
              <h1 className="text-3xl font-bold text-white mb-6">{video.title}</h1>
              <div className="aspect-video w-full bg-zinc-900 rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <VideoPlayer videoId={video.videoId} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}